import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowLeft, ExternalLink } from "lucide-react";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SocialMeta from "@/components/social/SocialMeta";
import { supabase } from "@/integrations/supabase/client";

type Anchor = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  website: string | null;
  location: string | null;
  tier: string;
  logo_url: string | null;
};

type Commitment = {
  id: string;
  category: string;
  headline: string;
  body: string | null;
  metric_value: string | null;
  metric_label: string | null;
};

export default function AnchorLanding() {
  const { slug } = useParams();

  const anchorQ = useQuery({
    enabled: !!slug,
    queryKey: ["anchor-landing", slug],
    queryFn: async (): Promise<Anchor | null> => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id, slug, name, tagline, description, website, location, tier, logo_url")
        .eq("slug", slug!)
        .eq("tier", "anchor")
        .eq("status", "active")
        .maybeSingle();
      if (error) throw error;
      return data as Anchor | null;
    },
  });

  const commitmentsQ = useQuery({
    enabled: !!anchorQ.data?.id,
    queryKey: ["anchor-landing-commitments", anchorQ.data?.id],
    queryFn: async (): Promise<Commitment[]> => {
      const { data, error } = await supabase
        .from("anchor_commitments")
        .select("id, category, headline, body, metric_value, metric_label")
        .eq("affiliate_id", anchorQ.data!.id)
        .eq("is_published", true)
        .order("sort_order")
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as Commitment[];
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (anchorQ.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="pt-40 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!anchorQ.data) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="pt-32 pb-20 container mx-auto px-6 max-w-2xl text-center space-y-4">
          <h1 className="font-display text-3xl">Anchor not found</h1>
          <p className="text-muted-foreground">
            This anchor page isn't published, or the organization is not at the Anchor tier.
          </p>
          <Button asChild variant="outline">
            <Link to="/partners/directory">Browse partner directory</Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const a = anchorQ.data;
  const commitments = (commitmentsQ.data ?? []).filter((c) => c.category === "commitment");
  const programs = (commitmentsQ.data ?? []).filter((c) => c.category === "program");
  const metrics = (commitmentsQ.data ?? []).filter((c) => c.category === "metric");

  return (
    <div className="min-h-screen bg-background">
      <SocialMeta
        title={`${a.name} — Anchor Partner | The Emerging Market`}
        description={a.tagline ?? `${a.name} is an anchor partner of The Emerging Market ecosystem in Alabama.`}
        path={`/anchors/${a.slug}`}
      />
      <SiteHeader />

      <main className="pt-32 pb-20">
        <section className="container mx-auto px-6 max-w-5xl">
          <Link to="/partners" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-3 w-3" /> Back to Partner Program
          </Link>

          <div className="flex items-start gap-6 flex-wrap">
            {a.logo_url && (
              <img src={a.logo_url} alt={`${a.name} logo`} className="h-24 w-24 rounded-lg object-cover border border-border" />
            )}
            <div className="flex-1 min-w-[280px]">
              <Badge className="bg-primary/15 text-primary border-primary/30 mb-3">Anchor Partner</Badge>
              <h1 className="font-display text-4xl md:text-5xl">{a.name}</h1>
              {a.tagline && <p className="text-lg text-muted-foreground mt-2">{a.tagline}</p>}
              {a.location && <p className="text-sm text-muted-foreground mt-1">{a.location}</p>}
              {a.website && (
                <a href={a.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-3">
                  Visit website <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>

          {a.description && (
            <p className="text-base leading-relaxed text-foreground/90 mt-8 max-w-3xl">{a.description}</p>
          )}
        </section>

        {metrics.length > 0 && (
          <section className="container mx-auto px-6 max-w-5xl mt-12">
            <h2 className="font-display text-2xl mb-4">Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {metrics.map((m) => (
                <Card key={m.id} className="p-6">
                  <div className="font-display text-3xl text-primary">{m.metric_value ?? m.headline}</div>
                  <div className="text-sm text-muted-foreground mt-1">{m.metric_label ?? m.body}</div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {commitments.length > 0 && (
          <section className="container mx-auto px-6 max-w-5xl mt-12">
            <h2 className="font-display text-2xl mb-4">Named commitments</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {commitments.map((c) => (
                <Card key={c.id} className="p-6">
                  <h3 className="font-display text-lg">{c.headline}</h3>
                  {c.body && <p className="text-sm text-muted-foreground mt-2">{c.body}</p>}
                </Card>
              ))}
            </div>
          </section>
        )}

        {programs.length > 0 && (
          <section className="container mx-auto px-6 max-w-5xl mt-12">
            <h2 className="font-display text-2xl mb-4">Sponsored programs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {programs.map((p) => (
                <Card key={p.id} className="p-6">
                  <h3 className="font-display text-lg">{p.headline}</h3>
                  {p.body && <p className="text-sm text-muted-foreground mt-2">{p.body}</p>}
                </Card>
              ))}
            </div>
          </section>
        )}

        <section className="container mx-auto px-6 max-w-5xl mt-16">
          <Card className="p-8 text-center bg-gradient-to-br from-primary/10 via-transparent to-transparent">
            <h2 className="font-display text-2xl">Anchor a piece of the work in Alabama</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">
              Partner with {a.name} to move real dollars, real scopes, and real outcomes through the emerging market.
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <Button asChild><Link to="/partners/apply">Become a partner</Link></Button>
              <Button asChild variant="outline"><Link to="/ideas">Explore ideas</Link></Button>
            </div>
          </Card>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}